import { useUnleashContext } from '@unleash/proxy-client-react';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useIntercom } from 'react-use-intercom';

import { getRecordingsUrl } from '../components/analytics/HotJar';
import Metrics from '../services/metrics';
import { isIntercomBooted, markIntercomBooted } from '../utils/intercomBootState';
import {
  addIntercomHideReason,
  removeIntercomHideReason,
} from '../utils/intercomLauncherVisibility';
import { shouldShowIntercomChat } from '../utils/intercomVisibility';
import useAccessBadge from './useAccessBadge';
import useConfirmedSession from './useConfirmedSession';
import { isFullySampledCountry } from '../services/telemetry';
import { Telemetry } from './useTelemetry';

function useIdentifyMetrics(uid: string) {
  const { boot: bootIntercom, update: updateIntercom } = useIntercom();
  const router = useRouter();

  // Stable per-hook-instance token for the launcher-hide reason this hook
  // owns — see `utils/intercomLauncherVisibility.ts`. Not a plain boolean
  // toggle: `useHideIntercom` (designer, live-game view) can have its own
  // reason active at the same time, and the launcher must stay hidden as
  // long as either one is.
  const orgGateHideReasonRef = useRef(Symbol('useIdentifyMetrics:org-gate'));

  // Release this hook's reason if it ever unmounts (e.g. logout swaps back
  // to the anonymous flow — see BackendServiceProvider) — otherwise a gated
  // org's hide reason would linger forever with nothing left to clear it.
  useEffect(() => {
    const reason = orgGateHideReasonRef.current;
    return () => removeIntercomHideReason(reason);
  }, []);

  const updateContext = useUnleashContext();

  // Impersonation is the one flag-context input the access badge cannot supply:
  // an impersonated session is deliberately indistinguishable from the target
  // user's on the badge (same uid, org, tenant, role). It lives only on the
  // session.
  //
  // useConfirmedSession (not raw useSession) because MetricsIdentifier is kept
  // mounted through a transient session-fetch failure (see
  // BackendServiceProvider's `hasBeenAuthenticated`/`confirmedUserId` gate):
  // during that blip `session.data` goes null even though nothing about
  // impersonation actually changed. Reading `isImpersonation` straight off
  // `session.data` would flip it to false for the duration of the blip and
  // let the very Mixpanel/Intercom calls below slip through unsuppressed.
  //
  // Computed synchronously during render (not via a state-setting effect) —
  // an effect would settle one tick late, leaving `isImpersonating` at its
  // initial `false` for the very first render and letting exactly the calls
  // this guards against fire once before catching up.
  const { session, isSessionInterrupted } = useConfirmedSession();
  const isImpersonatingRef = useRef(false);
  if (!isSessionInterrupted) {
    isImpersonatingRef.current = !!session.data?.isImpersonation;
  }
  const isImpersonating = isImpersonatingRef.current;

  const { accessBadge: userAccessBadge } = useAccessBadge();

  useEffect(() => {
    // Skip Mixpanel identification while impersonating — an admin's
    // impersonated activity should never get attributed to the target
    // user's Mixpanel profile.
    if (uid && !isImpersonating) {
      // Identify the user as soon we possible
      Metrics.identifyUser(uid);
    }
  }, [uid, isImpersonating]);

  useEffect(() => {
    if (userAccessBadge) {
      const plan = userAccessBadge.license.coarseLevel.toLowerCase();
      const subPlan = userAccessBadge.license.fineLevel.toLowerCase();
      // Skip Mixpanel profile/company updates while impersonating — an
      // admin's impersonated session shouldn't overwrite the target user's
      // Mixpanel/Intercom profile. Telemetry and the Intercom launcher
      // visibility rule below are unaffected — they aren't customer-facing
      // analytics profiles.
      if (!isImpersonating) {
        Metrics.setProfileAndCompanyAttributes({
          email: userAccessBadge.user.email,
          name: userAccessBadge.user.firstName
            ? `${userAccessBadge.user.firstName} ${userAccessBadge.user.lastName}`
            : undefined,
          plan,
          subPlan,
          aiAccessBlocked: userAccessBadge.license.aiAccessBlocked,
          curipodTrialReason: userAccessBadge.license.source,
          country: userAccessBadge.organization?.country || undefined,
          grades: userAccessBadge.user.profileInfo?.grades,
          subjects: userAccessBadge.user.profileInfo?.subjects,
          intendedUses: userAccessBadge.user.profileInfo?.intendedUses,
          schoolRole: userAccessBadge.user.profileInfo?.role,
          signUpOrigin: userAccessBadge.user.profileInfo?.signUpOrigin,
          ssoProvider: userAccessBadge.organization?.ssoProvider,
          // Mirror the org's `isInternational` onto the user profile and
          // as a Mixpanel super-property so every event the user emits is
          // tagged. Targets segmentation + feature-flag filters.
          isInternational: userAccessBadge.organization?.isInternational,
          // We only connect users to companies when they have become part of an org.
          company:
            userAccessBadge.organization &&
            (userAccessBadge.organization?.source === 'Curipod' ||
              userAccessBadge.organization?.source === 'MCH')
              ? {
                  schoolId: userAccessBadge.organization?.id,
                  schoolName: userAccessBadge.organization?.name,
                  createdAt: userAccessBadge.organization?.createdAt
                    ? Math.floor(userAccessBadge.organization?.createdAt / 1000)
                    : 0, // intercom api wants it in seconds for some reason...
                  createdBy: userAccessBadge.organization?.source,
                  districtName: userAccessBadge.organization?.parent?.name,
                  city: userAccessBadge.organization?.city,
                  region: userAccessBadge.organization?.region,
                  state:
                    userAccessBadge.organization?.country === 'US'
                      ? userAccessBadge.organization?.region
                      : undefined,
                  zipCode: userAccessBadge.organization?.primaryZipCode,
                  country: userAccessBadge.organization?.country,
                  isInternational: userAccessBadge.organization?.isInternational,
                  mbOrgUsageLink: Metrics.getMetabaseOrgUsageLink(
                    userAccessBadge.organization?.id,
                  ),
                  mbDistrictUsageLink: Metrics.getMetabaseDistrictOrgUsageLink(
                    userAccessBadge.organization?.parent?.id,
                  ),
                }
              : undefined,
          mbUserUsageLink: Metrics.getMetabaseUserUsageLink(userAccessBadge.user.email),
          hotjarRecordings: getRecordingsUrl({
            userAccessBadge,
            isUS: false,
            country: null,
          }),
        });

        /*
        Intercom only supports strings, booleans, numbers and dates. Data objects must be stringified json. There we can use string contains subject: english.
      */
        const intercomIdentity = {
          userId: userAccessBadge.user.id,
          email: userAccessBadge.user.email,
          name: userAccessBadge.user.firstName
            ? `${userAccessBadge.user.firstName} ${userAccessBadge.user.lastName}`
            : undefined,
          customAttributes: {
            plan,
            subPlan,
            aiAccessBlocked: userAccessBadge.license.aiAccessBlocked,
            curipodTrialReason: userAccessBadge.license.source,
            userRole: userAccessBadge.role,
            schoolRole: userAccessBadge.user.profileInfo?.role,
            grades: userAccessBadge.user.profileInfo?.grades
              ? JSON.stringify(userAccessBadge.user.profileInfo?.grades)
              : undefined,
            subjects: userAccessBadge.user.profileInfo?.subjects
              ? JSON.stringify(userAccessBadge.user.profileInfo?.subjects)
              : undefined,
            intendedUses: userAccessBadge.user.profileInfo?.intendedUses
              ? JSON.stringify(userAccessBadge.user.profileInfo?.intendedUses)
              : undefined,
            signUpOrigin: userAccessBadge.user.profileInfo?.signUpOrigin,
            accountType: undefined,
            ssoProvider: userAccessBadge.organization?.ssoProvider,
            country:
              userAccessBadge.organization?.country ?? userAccessBadge.tenant?.country,
            schoolName:
              userAccessBadge.organization?.name ?? userAccessBadge.tenant?.tenantName,
            districtId: userAccessBadge.organization?.parent?.id,
            districtName: userAccessBadge.organization?.parent?.name,
            region: userAccessBadge.organization?.region,
            state:
              userAccessBadge.organization?.country === 'US'
                ? userAccessBadge.organization?.region
                : undefined,
            schoolCity: userAccessBadge.organization?.city,
            schoolState: userAccessBadge.organization?.region,
            schoolZipCode: userAccessBadge.organization?.primaryZipCode,
            mbUserUsage: Metrics.getMetabaseUserUsageLink(userAccessBadge.user.email),
            mbOrgUsage: Metrics.getMetabaseOrgUsageLink(userAccessBadge.organization?.id),
            mbDistrictUsage: Metrics.getMetabaseDistrictOrgUsageLink(
              userAccessBadge.organization?.parent?.id,
            ),
            hotjarRecordings: getRecordingsUrl({
              userAccessBadge,
              isUS: false,
              country: null,
            }),

            mpIndividualUsage: Metrics.getUsageDashboardLink(
              'individual',
              userAccessBadge.user.id,
            ),
            mpDomainUsage: Metrics.getUsageDashboardLink(
              'domain',
              userAccessBadge.user.email.split('@')[1],
            ),
            impersonationLink: Metrics.getImpersonationLink(userAccessBadge.user.email),
            tenantId: userAccessBadge.tenant?.tenantId ?? router.query.tenantId,
            isInternational: userAccessBadge.organization?.isInternational,
          },
          // We only connect users to companies when they have become part of an org.
          company:
            userAccessBadge.organization &&
            (userAccessBadge.organization?.source === 'Curipod' ||
              userAccessBadge.organization?.source === 'MCH')
              ? {
                  companyId: userAccessBadge.organization?.id,
                  name: userAccessBadge.organization?.name,
                  customAttributes: {
                    organisationId: userAccessBadge.organization?.id,
                    createdAt: userAccessBadge.organization?.createdAt
                      ? Math.floor(userAccessBadge.organization?.createdAt / 1000)
                      : 0, // intercom api wants it in seconds for some reason...
                    createdBy: userAccessBadge.organization?.source,
                    districtName: userAccessBadge.organization?.parent?.name,
                    city: userAccessBadge.organization?.city,
                    region: userAccessBadge.organization?.region,
                    state:
                      userAccessBadge.organization?.country === 'US'
                        ? userAccessBadge.organization?.region
                        : undefined,
                    country: userAccessBadge.organization?.country,
                    zipCode: userAccessBadge.organization?.primaryZipCode,
                    isInternational: userAccessBadge.organization?.isInternational,
                    mbOrgUsage: Metrics.getMetabaseOrgUsageLink(
                      userAccessBadge.organization?.id,
                    ),
                    mbDistrictUsage: Metrics.getMetabaseDistrictOrgUsageLink(
                      userAccessBadge.organization?.parent?.id,
                    ),
                  },
                }
              : undefined,
        };

        // Intercom always boots for a logged-in user, regardless of the
        // country/region/plan rule below — `trackEvent` (lesson-generation
        // events, `StartGame`/`StartTestGame`) and the app's many explicit
        // "Chat with us" entry points (paywall modals, the floating
        // CustomIntercomButton, generator help chat, ...) all go through this
        // same widget, and silently break once it's shut down. The rule only
        // controls whether the *unprompted* launcher bubble shows — see
        // `utils/intercomLauncherVisibility.ts`.
        if (isIntercomBooted()) {
          updateIntercom(intercomIdentity);
        } else {
          bootIntercom(intercomIdentity);
          markIntercomBooted();
        }
      }

      // Country/region/plan rule for the automatic launcher — see
      // `shouldShowIntercomChat`. A US org sees it on Freemium too; a paid
      // org sees it outside the US too.
      const shouldShowChat = shouldShowIntercomChat({
        country: userAccessBadge.organization?.country,
        region: userAccessBadge.organization?.region,
        licenseCoarseLevel: userAccessBadge.license?.coarseLevel,
      });

      if (shouldShowChat) {
        removeIntercomHideReason(orgGateHideReasonRef.current);
      } else {
        addIntercomHideReason(orgGateHideReasonRef.current);
      }

      // The session's sampling verdict was drawn at page load from the
      // browser's timezone, which is a guess. The organisation's country is
      // the authoritative answer, and it only becomes available here — so a
      // US or Norwegian org whose timezone read as somewhere else (a VPN, a
      // travelling teacher, a mis-set machine) gets upgraded to full sampling
      // now.
      //
      // This cannot rescue what was already dropped: Faro's beforeSend hook
      // evaluates the session at PUSH time, so events fired between page load
      // and the access badge resolving are gone for good. That is exactly why
      // the timezone heuristic stays as the first line — it gets the common
      // case right with nothing lost, and this is the correction for the rest.
      const organizationCountry = userAccessBadge.organization?.country ?? null;
      Telemetry()?.setCustomAttribute('organizationCountry', organizationCountry);
      if (isFullySampledCountry(organizationCountry)) {
        Telemetry()?.forceFullSession();
      }

      Telemetry()?.setCustomAttribute('email', userAccessBadge?.user.email || null);
      Telemetry()?.setCustomAttribute('uid', userAccessBadge?.user.id || null);
      Telemetry()?.setUserId(userAccessBadge?.user.id || null);
      Telemetry()?.setCustomAttribute('plan', plan);
      Telemetry()?.setCustomAttribute('subPlan', subPlan);
      Telemetry()?.setCustomAttribute(
        'curipodTrialReason',
        userAccessBadge.license.source ?? null,
      );
      Telemetry()?.setCustomAttribute(
        'organizationId',
        userAccessBadge.organization?.id ?? null,
      );
      Telemetry()?.setCustomAttribute(
        'organizationName',
        userAccessBadge.organization?.name ?? null,
      );
      Telemetry()?.setCustomAttribute(
        'districtId',
        userAccessBadge.organization?.parent?.id || null,
      );
      Telemetry()?.setCustomAttribute(
        'districtName',
        userAccessBadge.organization?.parent?.name || null,
      );
    }
  }, [
    bootIntercom,
    updateIntercom,
    userAccessBadge,
    router.query.tenantId,
    isImpersonating,
  ]);

  useEffect(() => {
    // Unleash context
    if (userAccessBadge) {
      const plan = userAccessBadge.license.coarseLevel.toLowerCase();
      const subPlan = userAccessBadge.license.fineLevel.toLowerCase();
      updateContext({
        userId: userAccessBadge.user.id,
        properties: {
          email: userAccessBadge.user.email,
          role: userAccessBadge.role,
          tenantName: userAccessBadge.organization?.name || 'unknown',
          plan: plan,
          subPlan: subPlan,
          curipodTrialReason: userAccessBadge.license.source || 'unknown',
          districtId: userAccessBadge.organization?.parent?.id || 'unknown',
          districtName: userAccessBadge.organization?.parent?.name || 'unknown',
          state:
            userAccessBadge.organization?.country === 'US'
              ? userAccessBadge.organization?.region || 'unknown'
              : 'unknown',
          region: userAccessBadge.organization?.region || 'unknown',
          country: userAccessBadge.organization?.country ?? 'unknown',
          organizationId: userAccessBadge.organization?.id || 'unknown',
          organizationName: userAccessBadge.organization?.name || 'unknown',
          parentOrganizationId: userAccessBadge.organization?.parent?.id || 'unknown',
          // Unleash property values must be strings. Preserve the
          // tri-state — `'unknown'` is distinct from `'false'` so
          // feature-flag rules can target "answered no" separately from
          // "hasn't been asked yet". `== null` catches both `null` (SQL
          // NULL serialised by the backend) and `undefined` so we never
          // send the literal string `'null'` to Unleash.
          isInternational:
            userAccessBadge.organization?.isInternational == null
              ? 'unknown'
              : String(userAccessBadge.organization.isInternational),
          // Mirrors provisionFlags so the post-mount refetch agrees with the SSR
          // bootstrap. Without it, a flag constrained on isImpersonating would
          // evaluate true server-side and false here, so whatever it gates would
          // render on first paint and then vanish.
          isImpersonating: String(isImpersonating),
        },
      });
    }
  }, [userAccessBadge, updateContext, isImpersonating]);
}

export default useIdentifyMetrics;
